import type { RouteLocationNormalizedGeneric } from 'vue-router';

/**
 * @param route The route to read the brand overrides from. Defaults to the router's current
 * route, which is what components and plugins want. Route middleware must pass its own `to`:
 * while middleware runs, the router has not confirmed the navigation yet, so `currentRoute`
 * still points at the previous (on first render: the empty) route and would resolve — and
 * cache in state — a brand code without the `brandCode`/`brandcode`/`brandcodebe` overrides.
 */
export default function useBrandCode(route?: RouteLocationNormalizedGeneric) {
  const { public: publicConfig } = useRuntimeConfig();
  const router = useRouter();
  const currentRoute = route ?? router.currentRoute.value;
  const brandParam = queryAsString(currentRoute.params.brandCode);

  // Normalize query keys to lowercase to avoid case sensitivity
  const normalizedQuery = Object.fromEntries(
    Object.entries(currentRoute.query).map(([key, value]) => [key.toLowerCase(), value]),
  );

  const brandQuery = publicConfig.STAGE !== 'production' ? queryAsString(normalizedQuery.brandcode) : undefined;
  const host = import.meta.server ? useRequestHeader('Host') : window.location.host;
  const hostname = extractTopLevelDomain(host || '');
  const brandQueryBe = queryAsString(normalizedQuery.brandcodebe);
  const brandCodeQuery = brandQueryBe || brandQuery || brandParam;
  const brandCode = getBrandCode({ brandCode: brandCodeQuery, hostname });

  return useState<string>('brandCode', () => brandCode || '');
}
